// Confirm.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './Confirm.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalMessage from 'components/modal/message/ModalMessage';
import Modal from 'components/modal/Modal';

/**
 * Confirm
 * 
 * A simple confirmation modal component that can be used across the app for any action that requires user confirmation. It displays a title, a message, and a confirm button. The width of the modal adjusts for mobile screens.
 * 
 * @param {string} title - The title of the confirmation modal.
 * @param {string} message - The message to display in the modal body.
 * @param {boolean} isOpen - Whether the modal is currently open.
 * @param {function} setOpen - Function to set the open state of the modal.
 * @param {string} width - Optional width for the modal (defaults to 400px on desktop).
 * @param {function} onConfirm - Function to call when the confirm button is clicked.
 * @param {string} confirmLabel - Optional primary button label (defaults to Confirm).
 * @param {boolean} [busy=false] - Disables dismiss/confirm while an action is in flight.
 * @param {string} secondaryLabel - Optional secondary button label.
 * @param {function} onSecondary - Called when the secondary button is clicked.
 *
 * @returns {ReactNode} The rendered confirmation modal component.
 */
const Confirm = ({
  title,
  message,
  isOpen,
  setOpen,
  width,
  onConfirm,
  confirmLabel = 'Confirm',
  busy = false,
  secondaryLabel,
  onSecondary,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- RENDER ----- //

  return (
    <>
      <Modal
        title={title}
        isOpen={isOpen}
        height={"auto"}
        width={isMobile ? "100%" : width || "400px"}
        enableClose={!busy}
        onClose={() => {
          if (busy) return;
          setOpen(false);
        }}
      >

        <ModalForm>
          <ModalMessage text={message} />
        </ModalForm>

        <ModalButtons>
          {secondaryLabel && typeof onSecondary === 'function' ? (
            <Button
              enabled={!busy}
              label={secondaryLabel}
              onClick={onSecondary}
            />
          ) : null}

          <Button
            enabled={!busy}
            label={confirmLabel}
            onClick={onConfirm}
          />
        </ModalButtons>

      </Modal>
    </>
  );
};

// Dev Tools
Confirm.displayName = 'Confirm';

export default Confirm;
